import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { InferenceResultDetail, InferenceSampleResult, ExtractedAttribute } from '../../models/truth-management.model';
import { InferenceRunService } from '../../services/inference-run.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-inference-results',
  imports: [CommonModule, PdfViewerModule],
  templateUrl: './inference-results.html',
  styleUrl: './inference-results.scss',
})
export class InferenceResults implements OnInit {
  runId: number = 0;
  runDetail: InferenceResultDetail | null = null;
  selectedSample: InferenceSampleResult | null = null;
  
  // PDF Viewer properties
  pdfSrc: Uint8Array | null = null;
  currentPage: number = 1;
  totalPages: number = 0;
  zoom: number = 1.0;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private inferenceRunService: InferenceRunService,
    public authService: AuthService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.runId = +params['runId'];
      
      // Try to get run data from navigation state first
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;
      let runData = state?.['runData'];
      
      // If not in state, load from service (e.g., after refresh)
      if (!runData) {
        runData = this.inferenceRunService.getRunById(this.runId);
      }
      
      this.loadInferenceResults(runData);
    });
  }

  loadInferenceResults(runData?: any): void {
    if (!runData) {
      return;
    }

    // Load PDF if fileData is available
    if (runData.fileData) {
      this.pdfSrc = new Uint8Array(runData.fileData);
    }

    // Generate mock data for demonstration
    const attributes = this.generateMockAttributes();
    const samples: InferenceSampleResult[] = [];

    for (let i = 1; i <= 8; i++) {
      samples.push({
        sampleId: i,
        sampleName: `Sample ${i}`,
        documentType: runData.documentType || 'DTSAUSDU',
        pageRange: `${(i-1)*5 + 1}-${i*5}`,
        extractedAttributes: attributes.map((attr, idx) => ({
          ...attr,
          attributeId: idx + 1,
          extractedValue: Math.random() > 0.2 ? this.generateMockValue(attr.attributeLabel) : '',
          confidence: Math.floor(Math.random() * 30) + 70 // 70-100%
        }))
      });
    }

    this.runDetail = {
      runId: this.runId,
      documentTitle: runData.documentTitle || 'Untitled Run',
      documentType: runData.documentType || 'DTSAUSDU',
      modelName: runData.modelName || 'GPT-4',
      fileName: runData.fileName || 'document_analysis.pdf',
      timeOfRun: runData.timeOfRun || new Date(),
      samples: samples
    };

    // Auto-select first sample
    if (samples.length > 0) {
      this.selectSample(samples[0]);
    }
  }

  generateMockAttributes(): Array<{attributeLabel: string}> {
    return [
      { attributeLabel: 'Loan Number' },
      { attributeLabel: 'Borrower Name' },
      { attributeLabel: 'Property Address' },
      { attributeLabel: 'Loan Amount' },
      { attributeLabel: 'Interest Rate' },
      { attributeLabel: 'Loan Term' },
      { attributeLabel: 'Loan Type' },
      { attributeLabel: 'Property Type' },
      { attributeLabel: 'Occupancy Type' },
      { attributeLabel: 'Appraisal Value' },
      { attributeLabel: 'Credit Score' },
      { attributeLabel: 'DTI Ratio' },
      { attributeLabel: 'LTV Ratio' },
      { attributeLabel: 'Closing Date' },
      { attributeLabel: 'Origination Date' },
      { attributeLabel: 'Lender Name' },
      { attributeLabel: 'Servicer Name' },
      { attributeLabel: 'Document Date' }
    ];
  }

  generateMockValue(label: string): string {
    const valueMap: {[key: string]: string} = {
      'Loan Number': `LN${Math.floor(Math.random() * 1000000)}`,
      'Borrower Name': ['John Smith', 'Jane Doe', 'Robert Johnson', 'Mary Williams'][Math.floor(Math.random() * 4)],
      'Property Address': `${Math.floor(Math.random() * 9999)} Main St, City, ST 12345`,
      'Loan Amount': `$${(Math.random() * 500000 + 100000).toFixed(2)}`,
      'Interest Rate': `${(Math.random() * 3 + 3).toFixed(3)}%`,
      'Loan Term': ['15 years', '30 years', '20 years'][Math.floor(Math.random() * 3)],
      'Loan Type': ['Conventional', 'FHA', 'VA', 'USDA'][Math.floor(Math.random() * 4)],
      'Property Type': ['Single Family', 'Condo', 'Townhouse', 'Multi-Family'][Math.floor(Math.random() * 4)],
      'Occupancy Type': ['Primary Residence', 'Investment Property', 'Second Home'][Math.floor(Math.random() * 3)],
      'Appraisal Value': `$${(Math.random() * 600000 + 150000).toFixed(2)}`,
      'Credit Score': `${Math.floor(Math.random() * 150 + 650)}`,
      'DTI Ratio': `${(Math.random() * 20 + 25).toFixed(2)}%`,
      'LTV Ratio': `${(Math.random() * 15 + 75).toFixed(2)}%`,
      'Closing Date': new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toLocaleDateString(),
      'Origination Date': new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toLocaleDateString(),
      'Lender Name': ['ABC Bank', 'XYZ Mortgage', 'First National', 'Capital Lending'][Math.floor(Math.random() * 4)],
      'Servicer Name': ['ServiceCo', 'Mortgage Plus', 'Loan Services Inc'][Math.floor(Math.random() * 3)],
      'Document Date': new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toLocaleDateString()
    };
    
    return valueMap[label] || 'N/A';
  }

  selectSample(sample: InferenceSampleResult): void {
    this.selectedSample = sample;
    this.currentPage = 1;
    const range = sample.pageRange.split('-');
    this.totalPages = parseInt(range[1]) - parseInt(range[0]) + 1;
  }

  getConfidenceLevel(confidence: number): string {
    if (confidence >= 90) return 'high';
    if (confidence >= 75) return 'medium';
    return 'low';
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  zoomIn(): void {
    if (this.zoom < 3.0) {
      this.zoom += 0.1;
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.5) {
      this.zoom -= 0.1;
    }
  }

  onPdfLoadComplete(pdf: any): void {
    this.totalPages = pdf.numPages;
  }

  backToInferenceList(): void {
    this.router.navigate(['/data-extraction'], { fragment: 'inference' });
  }

  /**
   * Get current user email from sessionStorage
   */
}
